/**
 * @file FeedbackWidget.tsx
 * @author malcolmmzma
 */

import classNames from 'classnames';
import React, { useImperativeHandle, useState } from 'react';
import AdqIcon from '@tencent/adq-icons';
import { Frame, Text, Titlebar } from '@tencent/odc';
import { Button } from '@tencent/spaui';

import './index.less';

export type FeedbackWidgetType = 'init' | 'form' | 'done';

export interface IFeedbackWidgetProps {
  children?: React.ReactNode;
  show?: boolean;
  onSubmit?: () => void | Promise<void>;
  onClose?: () => void;
}

export interface IFeedbackWidgetRef {
  show: () => void;
  hide: () => void;
  changeStatus: (status: FeedbackWidgetType) => void;
}

const FeedbackWidget: React.ForwardRefRenderFunction<IFeedbackWidgetRef, IFeedbackWidgetProps> = (props, ref) => {
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<FeedbackWidgetType>('form');

  const { children, onClose: onCustomClose, onSubmit } = props;

  const show = () => {
    setVisible(true);
  };

  const hide = () => {
    setVisible(false);
  };

  const onClose = () => {
    setVisible(false);
    onCustomClose?.();
  };

  const changeStatus = (status: FeedbackWidgetType) => {
    setStatus(status);
    if (status === 'done') {
      setTimeout(() => {
        setVisible(false);
        setStatus('form');
      }, 2000);
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      show,
      hide,
      changeStatus,
    }),
    [],
  );

  return (
    <div
      className={classNames('x-fb-pop', {
        'x-fb-pop-mini': status === 'init',
        'x-fb-pop-form': status === 'form',
        'x-fb-pop-done': status === 'done',
        'x-fb-pop-show': visible,
      })}
    >
      <div className="x-fb-pop-system">
        <Button displayType="text" compound width={30} height={30} icon={<AdqIcon name="cancel" />} onClick={onClose} />
      </div>
      <div className="x-fb-pop-body">
        {/* MARK: 初始背景  */}
        {status === 'init' ? <i className="x-fb-skin" /> : null}

        {/* MARK: 表单  */}
        {status !== 'done' ? <Frame className="x-fb-form">{children}</Frame> : null}

        {/* MARK: 提示  */}
        {status === 'done' ? (
          <Frame className="x-fb-alert h-full flex-center flex-col gap-16">
            <div className="flex-center flex-col gap-8">
              <AdqIcon
                name="check-circle-filled"
                width={40}
                height={40}
                style={{
                  color: '#07C160',
                }}
              />
              <Titlebar size={4} title="感谢你的反馈" />
            </div>
            <Text size="small" label="弹窗将在 2 秒后自动退出" color="var(--color-disable-text,rgba(62,69,82,.36))" />
          </Frame>
        ) : null}
      </div>

      {status === 'form' ? (
        <div className="x-fb-pop-foot flex justify-end pt-0 pb-36 px-36">
          <Button displayType="primary" onClick={onSubmit}>
            提交
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default React.memo(React.forwardRef(FeedbackWidget));
